import { InvitesState } from './state';
import { invitesInitialState } from './initial-state';
import { InviteActions } from './action-creators';
import * as actions from './actions';
import { KeyTree } from '@webapp/types/common/key-tree';
import { TracInvite } from '@services/invites/types/trac-invite';

export const invitesReducer = (
  state: InvitesState = invitesInitialState,
  action: InviteActions
): InvitesState => {
  switch (action.type) {
    case actions.NEW_INVITE_DRAFT: {
      const maxKey: number = Math.max(
        -1,
        ...Object.keys(state.new[action.inviteType]).map((k) => Number(k))
      );
      const newKey = String(maxKey + 1);

      return {
        ...state,
        new: {
          ...state.new,
          [action.inviteType]: {
            ...state.new[action.inviteType],
            [newKey]: {
              type: action.inviteType,
              email_or_phone: '',
              metadata: {
                inviter: action.inviter,
              },
            },
          },
        },
      };
    }

    case actions.EDIT_INVITE_DRAFT: {
      return {
        ...state,
        new: {
          ...state.new,
          [action.inviteType]: {
            ...state.new[action.inviteType],
            [action.key]: {
              ...state.new[action.inviteType][action.key],
              ...action.invite,
            },
          },
        },
      };
    }

    case actions.REMOVE_INVITE_DRAFT: {
      const remaining = Object.keys(state.new[action.inviteType]).reduce(
        (memo, key) => {
          if (key === action.key) {
            return memo;
          }
          return {
            ...memo,
            [key]: state.new[action.inviteType][key],
          };
        },
        {}
      );

      return {
        ...state,
        new: {
          ...state.new,
          [action.inviteType]: remaining,
        },
      };
    }

    case actions.CREATE_INVITES_SUCCESS: {
      const newTree: KeyTree<TracInvite> = action.invites.reduce(
        (memo, i) => ({
          ...memo,
          [i.id]: i,
        }),
        {}
      );

      return {
        ...state,
        new: {
          ...state.new,
          [action.inviteType]: {},
        },
        current: {
          ...state.current,
          [action.inviteType]: {
            ...state.current[action.inviteType],
            ...newTree,
          },
        },
      };
    }

    case actions.GET_INVITES_SUCCESS: {
      const newTree = action.invites.reduce(
        (memo, i) => ({
          ...memo,
          [i.id]: i,
        }),
        {}
      );

      return {
        ...state,
        current: {
          ...state.current,
          [action.inviteType]: newTree,
        },
      };
    }

    case actions.GET_USER_INVITES_SUCCESS: {
      return {
        ...state,
        user: action.invites,
        loaded: true,
      };
    }

    case actions.GET_RECORD_LABEL_INVITES_SUCCESS: {
      return {
        ...state,
        recordLabel: {
          ...state.recordLabel,
          [action.inviteType]: action.invites,
        },
      };
    }

    case actions.CANCEL_INVITE: {
      const newTree = Object.keys(state.current[action.invite.type]).reduce(
        (memo, k) => {
          if (k === action.invite.id) {
            return memo;
          }

          return {
            ...memo,
            [k]: state.current[action.invite.type][k],
          };
        },
        {}
      );

      return {
        ...state,
        current: {
          ...state.current,
          [action.invite.type]: newTree,
        },
      };
    }

    case actions.ACCEPT_INVITE_SUCCESS: {
      return {
        ...state,
        user: state.user.map((i: TracInvite) => {
          if (i.id === action.invite.id) {
            return {
              ...i,
              status: 'accepted',
            };
          }
          return i;
        }),
      };
    }

    case actions.REJECT_INVITE_SUCCESS: {
      return {
        ...state,
        user: state.user.map((i: TracInvite) => {
          if (i.id === action.invite.id) {
            return {
              ...i,
              status: 'rejected',
            };
          }
          return i;
        }),
      };
    }

    case actions.CANCEL_INVITE_SUCCESS: {
      if (action.invite.type === 'record-label') {
        return {
          ...state,
          recordLabel: {
            ...state.recordLabel,
            'record-label': state.recordLabel['record-label'].filter(
              (x) => x.id !== action.invite.id
            ),
          },
        };
      }
      return state;
    }

    case actions.RESET_INVITES_STORE:
      return { ...invitesInitialState };

    default:
      return state;
  }
};
